import { useEffect, useRef } from 'react';

// ONE shared "don't trust a stale snapshot" hook (denorm campaign Phase 6 / EX3).
//
// A claimable list (the pending-task pool, the approved-expense pool) can go
// stale the moment another user claims/releases something. The mutations
// themselves are atomic server-side; this hook keeps the *view* fresh so one
// finance user never clicks a task another already took. It calls `refetch`:
//   - every `intervalMs` (default 30s) WHILE the tab is visible, and
//   - the moment the tab regains focus / becomes visible again.
// It deliberately does NOT poll a hidden/backgrounded tab (wasteful — it
// refetches on return instead) and does NOT fetch on mount (the pages already
// load their own data on mount; a mount fetch here would just double it).
//
// `refetch` is held in a ref so a fresh function identity each render doesn't
// tear down and rebuild the interval. Pass `enabled: false` to suspend it
// (e.g. a role that has no claimable work on the page).
export default function useAutoRefresh(refetch, { intervalMs = 30000, enabled = true } = {}) {
  const savedRefetch = useRef(refetch);
  useEffect(() => {
    savedRefetch.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!enabled) return undefined;
    const refetchIfVisible = () => {
      if (document.visibilityState === 'visible') savedRefetch.current?.();
    };
    const intervalId = setInterval(refetchIfVisible, intervalMs);
    // focus + visibilitychange both cover "user came back to this tab".
    window.addEventListener('focus', refetchIfVisible);
    document.addEventListener('visibilitychange', refetchIfVisible);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', refetchIfVisible);
      document.removeEventListener('visibilitychange', refetchIfVisible);
    };
  }, [intervalMs, enabled]);
}
